import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  vendorGroupsApi,
  VendorGroupRegion,
  ShipmentType,
  CreateVendorGroupDto,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const SHIPMENT_TYPES: { value: ShipmentType; label: string }[] = [
  { value: 'AIR', label: 'Air' },
  { value: 'OCEAN_FCL', label: 'Ocean FCL' },
  { value: 'OCEAN_LCL', label: 'Ocean LCL' },
  { value: 'FTL', label: 'Truck FTL' },
  { value: 'LTL', label: 'Truck LTL' },
  { value: 'RAIL', label: 'Rail' },
  { value: 'INTERMODAL', label: 'Intermodal' },
];

const REGIONS: VendorGroupRegion[] = [
  'North America',
  'South America',
  'Europe',
  'Asia Pacific',
  'Middle East',
  'Africa',
  'Global',
];

export default function VendorGroupsNew() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateVendorGroupDto>({
    name: '',
    description: '',
    shipmentTypes: [],
    regions: [],
  });
  const [saving, setSaving] = useState(false);

  const toggleShipmentType = (type: ShipmentType) => {
    setFormData((prev) => ({
      ...prev,
      shipmentTypes: prev.shipmentTypes?.includes(type)
        ? prev.shipmentTypes.filter((t) => t !== type)
        : [...(prev.shipmentTypes || []), type],
    }));
  };

  const toggleRegion = (region: VendorGroupRegion) => {
    setFormData((prev) => ({
      ...prev,
      regions: prev.regions?.includes(region)
        ? prev.regions.filter((r) => r !== region)
        : [...(prev.regions || []), region],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Please provide a group name');
      return;
    }

    try {
      setSaving(true);
      await vendorGroupsApi.create(formData);
      toast.success('Vendor group created successfully');
      navigate('/tms/vendor-groups');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="New Vendor Group"
        description="Name and carrier selection required to define a group for quotation invitations."
        actions={
          <Button variant="outline" onClick={() => navigate('/tms/vendor-groups')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Vendor Groups
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Group Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="name">Group Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="North America Carriers"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the purpose of this vendor group..."
                rows={3}
              />
            </div>
          </div>

          {/* Shipment Types */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Applicable Shipment Types</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-3">
              {SHIPMENT_TYPES.map((type) => (
                <div key={type.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${type.value}`}
                    checked={formData.shipmentTypes?.includes(type.value) ?? false}
                    onCheckedChange={() => toggleShipmentType(type.value)}
                  />
                  <Label htmlFor={`type-${type.value}`} className="cursor-pointer font-normal">
                    {type.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Regions */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Applicable Regions</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-3">
              {REGIONS.map((region) => (
                <div key={region} className="flex items-center space-x-2">
                  <Checkbox
                    id={`region-${region}`}
                    checked={formData.regions?.includes(region) ?? false}
                    onCheckedChange={() => toggleRegion(region)}
                  />
                  <Label htmlFor={`region-${region}`} className="cursor-pointer font-normal">
                    {region}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/tms/vendor-groups')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Vendor Group'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
